export interface AreaPhoto {
  url: string
  alt: string
  caption: string
  credit?: string
  creditUrl?: string
}

export interface AreaEveningSpot {
  name: string
  locality: string
  timing: string
  detail: string
  businessSlug?: string
}

/** Optional editorial block (e.g. seasonal nightlife on an area page). */
export interface AreaEveningFeature {
  eyebrow: string
  title: string
  lede: string
  image?: AreaPhoto
  paragraphs: string[]
  spots: AreaEveningSpot[]
  tips: string[]
  disclaimer: string
}

export interface AreaGuide {
  slug: string
  name: string
  label: string
  setting: string
  positioning: string
  seo: {
    title: string
    description: string
  }
  coordinates: {
    lat: number
    lng: number
  }
  googleMapsUrl: string
  neighbourhoods: string[]
  hero: AreaPhoto
  gallery: AreaPhoto[]
  introduction: string[]
  facts: Array<{ label: string; value: string }>
  highlights: Array<{ title: string; description: string }>
  bestFor: string[]
  planning: {
    base: string
    gettingThere: string
    gettingAround: string
    bestTime: string
  }
  featuredBusinessSlugs: string[]
  faqs: Array<{ question: string; answer: string }>
  sources: Array<{ label: string; url: string }>
  factCheckedAt: string
  eveningFeature?: AreaEveningFeature
}

const FACT_CHECKED_AT = '2026-07-21'

export const areas: AreaGuide[] = [
  {
    slug: 'pantai-cenang',
    name: 'Pantai Cenang',
    label: 'Beach district',
    setting: 'Langkawi’s most walkable visitor coast',
    positioning:
      'Choose Pantai Cenang for an easy island base where beach time, food, family attractions and sunset plans sit close together.',
    seo: {
      title: 'Pantai Cenang Area Guide | Beach, Food & Stays',
      description:
        'Plan Pantai Cenang: beach days, sunset strips, evening fire shows, food, stays and editor-verified places on Langkawi’s main visitor coast.',
    },
    coordinates: { lat: 6.2955936, lng: 99.7228039 },
    googleMapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Pantai%20Cenang%2C%20Langkawi',
    neighbourhoods: ['Pantai Cenang'],
    hero: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/54/Pantai_Cenang_1.jpg/1920px-Pantai_Cenang_1.jpg',
      alt: 'Wide sandy shoreline and calm water at Pantai Cenang in Langkawi',
      caption: 'Pantai Cenang’s west-facing beach and visitor district.',
      credit: 'CyberTroopers · CC BY-SA 4.0',
      creditUrl: 'https://commons.wikimedia.org/wiki/File:Pantai_Cenang_1.jpg',
    },
    gallery: [
      {
        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/54/Pantai_Cenang_3.jpg/1920px-Pantai_Cenang_3.jpg',
        alt: 'Beach activity and sea view in the Pantai Cenang district',
        caption: 'Cenang becomes livelier through the afternoon.',
        credit: 'CyberTroopers · CC BY-SA 4.0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Pantai_Cenang_3.jpg',
      },
      {
        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Pantai_Cenang%2C_Langkawi_01.jpg/1920px-Pantai_Cenang%2C_Langkawi_01.jpg',
        alt: 'Visitors walking beside the beach at Pantai Cenang Langkawi',
        caption: 'The district combines sand, shops and casual dining.',
        credit: 'Nahid Sultan · CC BY-SA 4.0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Pantai_Cenang,_Langkawi_01.jpg',
      },
    ],
    introduction: [
      'Pantai Cenang is Langkawi’s busiest visitor district and its easiest place to build a flexible day. The public beach runs alongside a dense strip of hotels, restaurants, cafés, shops and services, with Underwater World at the southern end.',
      'Stay here when convenience matters more than seclusion. Mornings are suited to walking and swimming in safe conditions, afternoons bring more activity, and the west-facing shore makes sunset the district’s daily meeting point.',
    ],
    facts: [
      { label: 'Atmosphere', value: 'Social, walkable and visitor-focused' },
      { label: 'Ideal stay', value: 'First visit or car-light beach break' },
      { label: 'Airport', value: 'About 10–15 minutes by road' },
      { label: 'Plan for', value: 'Beach, sunset, food and shopping' },
    ],
    highlights: [
      {
        title: 'A simple beach day',
        description:
          'Move between the sand, refreshments and indoor attractions without a long transfer.',
      },
      {
        title: 'Sunset energy',
        description:
          'The shoreline and beachfront venues become the focus from late afternoon.',
      },
      {
        title: 'Family flexibility',
        description:
          'Underwater World and casual dining offer useful alternatives to midday heat or rain.',
      },
      {
        title: 'Walkable evenings',
        description:
          'Restaurants, beach bars and shops let visitors continue after sunset on foot.',
      },
    ],
    bestFor: [
      'First-time visitors',
      'Families',
      'Sunset',
      'Food discovery',
      'Evening fire shows',
    ],
    planning: {
      base: 'A practical base for visitors who want the widest choice within a compact coastal district.',
      gettingThere:
        'Pantai Cenang is roughly seven kilometres from Langkawi International Airport. Taxi, e-hailing and rental vehicles are the usual arrival options.',
      gettingAround:
        'The central strip is walkable, but pavements can be uneven and traffic grows around sunset. Use short rides for places beyond the main beach road.',
      bestTime:
        'Go early for cooler sand and quieter swimming conditions, or arrive from 17:30 for sunset, dinner and the evening atmosphere.',
    },
    featuredBusinessSlugs: [
      'underwater-world-langkawi',
      'pelangi-beach-resort-spa-langkawi',
      'orkid-ria-seafood-restaurant',
      'kalut-cafe-and-bar',
      'yellow-beach-club-langkawi',
    ],
    eveningFeature: {
      eyebrow: 'After sunset',
      title: 'Fire shows on the Cenang strip',
      lede:
        'Flame performances are a familiar part of Pantai Cenang nights — from beach-bar shows to informal acts near the open plaza. Times shift with weather, crowds and each venue’s roster, so use this as orientation and confirm on the night.',
      image: {
        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Street_fire_show_at_Kozhikode_Beach.jpg/1920px-Street_fire_show_at_Kozhikode_Beach.jpg',
        alt: 'Fire performer with poi on a beach at dusk',
        caption:
          'Beachside fire acts at dusk — the kind of performance Pantai Cenang bars and street performers often stage after sunset (illustrative).',
        credit: 'Vis M · CC BY-SA 4.0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Street_fire_show_at_Kozhikode_Beach.jpg',
      },
      paragraphs: [
        'Most scheduled acts sit along the main beach road between late afternoon and roughly 9 p.m., with a second cluster on Pantai Tengah a short ride south. Some visitors book a table for dinner and drinks; others watch from the public sand, especially near open gathering points.',
        'Performances can involve poi, batons, fire-eating or acrobatics close to the audience. Venues usually mark a safety buffer — stay behind it, supervise children and avoid stepping into the performance arc for photos.',
        'Langkawi App lists verified bars that host shows when we have them on file. We do not run these events; always follow staff instructions and check venue social pages if rain is forecast.',
      ],
      spots: [
        {
          name: 'Kalut Cafe And Bar',
          locality: 'Pantai Cenang (beachfront)',
          timing: 'Often around 7:30 p.m. on busy evenings',
          detail:
            'Sand-level beanbags and a party atmosphere make this one of the best-known scheduled shows on the strip. Arrive early if you want a front-row seat with a drink order.',
          businessSlug: 'kalut-cafe-and-bar',
        },
        {
          name: 'Yellow Beach Club Langkawi',
          locality: 'Pantai Cenang',
          timing: 'Typically in the 8 p.m. hour when the club is open',
          detail:
            'Pairs flame acts with sunset drinks on the sand. Check which nights the club is trading — Tuesday is commonly closed.',
          businessSlug: 'yellow-beach-club-langkawi',
        },
        {
          name: 'Hidden Langkawi',
          locality: 'Pantai Tengah',
          timing: 'Often advertised between 8:30 and 9 p.m.',
          detail:
            'A slightly more polished beachfront setting south of Cenang. Worth the short taxi if you want a date-night mood away from the busiest bar cluster.',
        },
        {
          name: 'Dataran Cenang',
          locality: 'Central Pantai Cenang',
          timing: 'From late afternoon into the evening; busier toward 9 p.m.',
          detail:
            'Open paved space near the beach where informal performers sometimes practise and perform for tips. No cover charge, but respect other viewers and keep clear of the flame zone.',
        },
        {
          name: 'Anak Hantu Beach Bar',
          locality: 'Pantai Cenang',
          timing: 'Evenings; schedule varies',
          detail:
            'A low-key beach bar that occasionally folds fire entertainment into its nightly vibe. Good option if you prefer a smaller, rustic crowd.',
        },
        {
          name: 'Love Reggae Bar Cafe',
          locality: 'Pantai Cenang',
          timing: 'Saturday nights are commonly highlighted',
          detail:
            'Reggae-led nightlife with periodic fire segments. Treat Saturday listings as likely, not guaranteed — message the bar if you are planning around it.',
        },
      ],
      tips: [
        'Aim to arrive 20–30 minutes before the hour you hope to see flames if you need a venue table.',
        'Wind and rain can cancel outdoor fire acts with little online notice — have a backup dinner plan.',
        'Free viewing from the beach is often possible outside a bar’s roped seating, but do not block staff or performers.',
        'If you enjoy a street performance, a small tip is customary; for venue shows, budget for food or drinks.',
      ],
      disclaimer:
        'Show times and formats change seasonally and are decided by each venue or performer. Confirm directly with the business before you travel. Stay behind safety markers and never handle equipment.',
    },
    faqs: [
      {
        question: 'Is Pantai Cenang a good area to stay without a car?',
        answer:
          'Yes. It is Langkawi’s most practical car-light area because the beach, restaurants, shops and several attractions sit along the same district.',
      },
      {
        question: 'Is Pantai Cenang suitable for families?',
        answer:
          'Yes. Families can combine beach time with Underwater World, casual meals and short transfers, while still checking sea conditions before swimming.',
      },
      {
        question: 'When is Pantai Cenang busiest?',
        answer:
          'Late afternoon through dinner is usually the busiest period, especially around sunset, weekends and holiday dates.',
      },
      {
        question: 'How far is Pantai Cenang from the airport?',
        answer:
          'The district is approximately seven kilometres from Langkawi International Airport; actual driving time depends on traffic and the exact hotel.',
      },
      {
        question: 'Where can I watch fire shows in Pantai Cenang?',
        answer:
          'Evening flame acts run along the beach strip and at open areas such as Dataran Cenang, usually between sunset and about 9 p.m. Schedules vary by venue and weather — use the fire-show section on this guide and confirm with bars on the night.',
      },
    ],
    sources: [
      {
        label: 'Tourism Malaysia — Langkawi beaches',
        url: 'https://ebrochures.malaysia.travel/en/islands-and-beaches/langkawi-kedah/',
      },
    ],
    factCheckedAt: FACT_CHECKED_AT,
  },
  {
    slug: 'pantai-tengah',
    name: 'Pantai Tengah',
    label: 'Quiet coast',
    setting: 'A calmer beach base beside Cenang',
    positioning:
      'Choose Pantai Tengah for resort stays, sunset dining and a slower coastal rhythm while keeping Cenang within a short ride.',
    seo: {
      title: 'Pantai Tengah Area Guide | Stays, Food & Beaches',
      description:
        'Explore Pantai Tengah in Langkawi with advice on stays, beaches, dining, transport, nearby Pantai Cenang and editor-verified places.',
    },
    coordinates: { lat: 6.2796, lng: 99.7318 },
    googleMapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Pantai%20Tengah%2C%20Langkawi',
    neighbourhoods: ['Pantai Tengah'],
    hero: {
      url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1800&q=85',
      alt: 'Tropical shoreline representing the relaxed coast at Pantai Tengah Langkawi',
      caption: 'Pantai Tengah offers a quieter coastal base south of Cenang.',
      credit: 'Unsplash',
      creditUrl: 'https://unsplash.com/photos/KMn4VEeEPR8',
    },
    gallery: [
      {
        url: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=82',
        alt: 'Coastal road and tropical landscape near Pantai Tengah Langkawi',
        caption:
          'Short road journeys connect Tengah with Cenang and the airport.',
        credit: 'Unsplash',
        creditUrl: 'https://unsplash.com/',
      },
      {
        url: 'https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?auto=format&fit=crop&w=1400&q=82',
        alt: 'Resort pool and tropical coast illustrating stays in Pantai Tengah',
        caption: 'The area is shaped by resort stays and relaxed evenings.',
        credit: 'Unsplash',
        creditUrl: 'https://unsplash.com/',
      },
    ],
    introduction: [
      'Pantai Tengah sits immediately south of Pantai Cenang but feels more dispersed. Resorts, villas, restaurants and spas line the coast and connecting roads, creating a quieter base for couples and repeat visitors.',
      'The trade-off is convenience: some places are walkable, but the area is less continuous than Cenang. A short taxi, e-hailing ride or rental vehicle makes it easier to reach restaurants, activities and neighbouring beaches.',
    ],
    facts: [
      { label: 'Atmosphere', value: 'Relaxed, resort-led and less crowded' },
      { label: 'Ideal stay', value: 'Couples and slower beach trips' },
      { label: 'Airport', value: 'About 15–20 minutes by road' },
      { label: 'Nearby', value: 'Pantai Cenang is a short ride north' },
    ],
    highlights: [
      {
        title: 'Quieter coast',
        description:
          'A more dispersed shoreline gives the area a slower pace than central Cenang.',
      },
      {
        title: 'Resort choice',
        description:
          'Contemporary resorts and smaller stays support different trip styles.',
      },
      {
        title: 'Sunset dining',
        description:
          'West-coast viewpoints and evening venues make late afternoon the key period.',
      },
      {
        title: 'Close to Cenang',
        description:
          'Visitors can reach Cenang’s denser food and shopping options without staying in its busiest section.',
      },
    ],
    bestFor: ['Couples', 'Resort stays', 'Sunset dining', 'Repeat visitors'],
    planning: {
      base: 'A balanced base for visitors who want coastal calm with the option to dip into Cenang.',
      gettingThere:
        'Use a taxi, e-hailing service or rental vehicle from the airport. Journey time varies by the exact resort and evening traffic.',
      gettingAround:
        'Do not assume the whole district is comfortably walkable. Short rides are useful between resorts, restaurants and Cenang.',
      bestTime:
        'Late afternoon suits beach light and sunset dining; mornings are better for a quieter walk or scheduled activity.',
    },
    featuredBusinessSlugs: [
      'aloft-langkawi-pantai-tengah',
      'parkroyal-langkawi-resort',
      'rockafellers-langkawi',
      'devs-adventure-tours',
    ],
    faqs: [
      {
        question: 'Is Pantai Tengah quieter than Pantai Cenang?',
        answer:
          'Generally yes. Pantai Tengah is more dispersed and resort-led, while Cenang has a denser commercial strip and more foot traffic.',
      },
      {
        question: 'Can I walk from Pantai Tengah to Pantai Cenang?',
        answer:
          'Some southern Cenang and northern Tengah locations are close, but heat, traffic and inconsistent pavements can make a short ride more comfortable.',
      },
      {
        question: 'Who should stay in Pantai Tengah?',
        answer:
          'It works well for couples, repeat visitors and travellers who prefer a calmer resort base while remaining near Cenang.',
      },
      {
        question: 'Does Pantai Tengah have restaurants and nightlife?',
        answer:
          'Yes, the area has restaurants and bars, though they are spread out rather than concentrated along one continuous strip.',
      },
      {
        question: 'Do I need transport in Pantai Tengah?',
        answer:
          'Transport is useful. Confirm what is walkable from your accommodation and use e-hailing, taxis or a rental vehicle for the wider district.',
      },
    ],
    sources: [
      {
        label: 'Tourism Malaysia — Langkawi destination',
        url: 'https://www.malaysia.travel/explore/langkawi',
      },
    ],
    factCheckedAt: FACT_CHECKED_AT,
  },
  {
    slug: 'kuah',
    name: 'Kuah',
    label: 'Town centre',
    setting: 'Langkawi’s practical waterfront town',
    positioning:
      'Choose Kuah for ferry access, local food, shopping and everyday island services rather than a resort-beach atmosphere.',
    seo: {
      title: 'Kuah Area Guide | Food, Shopping & Ferry Access',
      description:
        'Plan a visit or stay in Kuah, Langkawi, with ferry, transport, food, shopping and waterfront advice plus editor-verified nearby places.',
    },
    coordinates: { lat: 6.3266, lng: 99.8432 },
    googleMapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Kuah%2C%20Langkawi',
    neighbourhoods: ['Kuah'],
    hero: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Dataran_Lang_2018.jpg/1920px-Dataran_Lang_2018.jpg',
      alt: 'Eagle Square and waterfront terraces in Kuah Langkawi',
      caption: 'Kuah’s waterfront landmarks sit close to the ferry terminal.',
      credit: 'Ttt.Osakanman · CC BY-SA 4.0',
      creditUrl:
        'https://commons.wikimedia.org/wiki/File:Dataran_Lang_2018.jpg',
    },
    gallery: [
      {
        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/Langkawi_Eagle_Square_-_Kuah_Town._%2836668381331%29.jpg/1920px-Langkawi_Eagle_Square_-_Kuah_Town._%2836668381331%29.jpg',
        alt: 'Giant eagle landmark facing the harbour at Kuah Langkawi',
        caption: 'Dataran Lang is Kuah’s best-known waterfront landmark.',
        credit: 'Bernard Spragg. NZ · CC0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Langkawi_Eagle_Square_-_Kuah_Town._(36668381331).jpg',
      },
      {
        url: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1400&q=82',
        alt: 'Local restaurant tables representing food discovery in Kuah Langkawi',
        caption:
          'Kuah is one of the island’s strongest areas for local dining.',
        credit: 'Unsplash',
        creditUrl: 'https://unsplash.com/',
      },
    ],
    introduction: [
      'Kuah is Langkawi’s main town and administrative centre. Ferry passengers arrive nearby, residents use its shops and services, and visitors come for local restaurants, duty-free shopping, Dataran Lang and the harbourfront.',
      'It is a practical rather than beach-led base. Choose Kuah when ferry convenience, town amenities and local food matter more than stepping directly onto a swimming beach.',
    ],
    facts: [
      { label: 'Atmosphere', value: 'Local, practical and waterfront-facing' },
      { label: 'Ideal stay', value: 'Ferry trips, food and island errands' },
      { label: 'Airport', value: 'About 25–35 minutes by road' },
      { label: 'Known for', value: 'Jetty, shopping and local restaurants' },
    ],
    highlights: [
      {
        title: 'Ferry convenience',
        description:
          'Kuah Jetty makes the area useful for arrivals, departures and short island stays.',
      },
      {
        title: 'Local food',
        description:
          'Chinese seafood, Malay cooking and casual local restaurants are spread around town.',
      },
      {
        title: 'Waterfront landmarks',
        description:
          'Dataran Lang, Eagle Square and nearby parks provide an easy harbourfront circuit.',
      },
      {
        title: 'Everyday services',
        description:
          'Shopping centres, banks, clinics and practical services are easier to find here than in resort areas.',
      },
    ],
    bestFor: ['Ferry passengers', 'Local food', 'Shopping', 'Practical stays'],
    planning: {
      base: 'A practical town base for ferry access, errands and visitors who prioritise food over a resort beach.',
      gettingThere:
        'Kuah Jetty is close to the town centre. The airport is reached by taxi, e-hailing or rental vehicle across the island.',
      gettingAround:
        'Individual clusters are walkable, but the wider town is spread out. Use short rides between the jetty, dining areas and shopping centres.',
      bestTime:
        'Visit the waterfront early or late to avoid the strongest heat, then use the evening for local dining and shopping.',
    },
    featuredBusinessSlugs: [
      'maha-tower-langkawi',
      'dataran-lang',
      'wonderland-food-store',
    ],
    faqs: [
      {
        question: 'Is Kuah a good place to stay in Langkawi?',
        answer:
          'Kuah is a good practical base for ferry access, local food, shopping and services, but it is not the best choice for a walk-out swimming beach.',
      },
      {
        question: 'How far is Kuah from Langkawi Airport?',
        answer:
          'Allow roughly 25–35 minutes by road depending on traffic and the exact pickup point.',
      },
      {
        question: 'What is there to do in Kuah?',
        answer:
          'Visit Dataran Lang and the waterfront, explore local restaurants, shop for practical or duty-free items and check the night-market schedule.',
      },
      {
        question: 'Is Kuah walkable?',
        answer:
          'Parts of the centre and waterfront are walkable, but the town is spread out and short vehicle trips are often more practical.',
      },
      {
        question: 'Is Kuah near a beach?',
        answer:
          'Kuah faces the harbour rather than a main swimming beach. Plan a drive to Cenang, Tanjung Rhu or another suitable coast for beach time.',
      },
    ],
    sources: [
      {
        label: 'Tourism Malaysia — Langkawi destination',
        url: 'https://www.malaysia.travel/explore/langkawi',
      },
    ],
    factCheckedAt: FACT_CHECKED_AT,
  },
  {
    slug: 'padang-matsirat',
    name: 'Padang Matsirat',
    label: 'Village and airport',
    setting: 'Arrival routes, village life and mountain access',
    positioning:
      'Choose Padang Matsirat for airport convenience, everyday island life and fast access towards SkyCab, Pantai Kok and western Langkawi.',
    seo: {
      title: 'Padang Matsirat Area Guide | Airport & West Langkawi',
      description:
        'Explore Padang Matsirat near Langkawi Airport with transport advice, village context, western attractions and editor-verified nearby places.',
    },
    coordinates: { lat: 6.354, lng: 99.7348 },
    googleMapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Padang%20Matsirat%2C%20Langkawi',
    neighbourhoods: ['Padang Matsirat'],
    hero: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Langkawi_Cable_Car_SKYCAB.jpg/1920px-Langkawi_Cable_Car_SKYCAB.jpg',
      alt: 'Langkawi SkyCab above rainforest near the Padang Matsirat side of the island',
      caption:
        'Padang Matsirat is a useful approach to western Langkawi attractions.',
      credit: 'Wikimedia Commons',
      creditUrl:
        'https://commons.wikimedia.org/wiki/File:Langkawi_Cable_Car_SKYCAB.jpg',
    },
    gallery: [
      {
        url: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=82',
        alt: 'Green rural landscape representing villages around Padang Matsirat Langkawi',
        caption: 'Village roads and open landscapes shape the wider district.',
        credit: 'Unsplash',
        creditUrl: 'https://unsplash.com/',
      },
      {
        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Langkawi_Cable_Car_SKYCAB.jpg/1920px-Langkawi_Cable_Car_SKYCAB.jpg',
        alt: 'Langkawi SkyCab gondolas above the rainforest near Padang Matsirat',
        caption:
          'Padang Matsirat is the practical base for western mountain attractions.',
        credit: 'Ttt.Osakanman · CC BY-SA 4.0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Langkawi_Cable_Car_SKYCAB.jpg',
      },
    ],
    introduction: [
      'Padang Matsirat covers a broad western part of Langkawi around the airport and village roads leading towards Pantai Kok and the Machinchang attractions. It feels more local and dispersed than the beach districts.',
      'This is a useful arrival-day base or transit area, but not a single walkable visitor centre. A rental vehicle, taxi or e-hailing service is important for linking attractions, cafés and accommodation.',
    ],
    facts: [
      { label: 'Atmosphere', value: 'Local, dispersed and transport-oriented' },
      { label: 'Ideal stay', value: 'Airport nights and western itineraries' },
      { label: 'Airport', value: 'Within or beside the wider district' },
      { label: 'Access', value: 'Useful for SkyCab and Pantai Kok routes' },
    ],
    highlights: [
      {
        title: 'Airport convenience',
        description:
          'Short transfers make the district practical around early departures or late arrivals.',
      },
      {
        title: 'Western gateway',
        description:
          'Roads continue towards Pantai Kok, Oriental Village, SkyCab and Machinchang.',
      },
      {
        title: 'Village context',
        description:
          'Local shops, roads and settlements show a more everyday side of Langkawi.',
      },
      {
        title: 'Active outings',
        description:
          'Cable-car and rainforest activities make the wider area useful for adventure itineraries.',
      },
    ],
    bestFor: [
      'Airport convenience',
      'Adventure',
      'Rental-car trips',
      'Arrival day',
    ],
    planning: {
      base: 'A practical base for airport timing or an itinerary focused on western Langkawi rather than beach nightlife.',
      gettingThere:
        'Langkawi International Airport sits beside the district. Confirm whether your accommodation provides a transfer or arrange a taxi, e-hailing ride or rental vehicle.',
      gettingAround:
        'Distances are too spread out for a walking itinerary. Build the day around a vehicle and cluster stops along the same western route.',
      bestTime:
        'Start mountain and outdoor activities early, then leave buffer time for weather, queues and airport traffic.',
    },
    featuredBusinessSlugs: [
      'langkawi-skycab',
      'dream-forest-langkawi',
      'umgawa-zipline-eco-adventures',
      'the-danna-langkawi',
      'berjaya-langkawi-resort',
    ],
    faqs: [
      {
        question: 'Is Padang Matsirat close to Langkawi Airport?',
        answer:
          'Yes. The airport sits beside the wider Padang Matsirat area, making it useful for arrival and departure planning.',
      },
      {
        question: 'Is Padang Matsirat a beach area?',
        answer:
          'No. It is a broad village and airport district, though roads lead towards Pantai Kok and other western coastal areas.',
      },
      {
        question: 'Do I need a car in Padang Matsirat?',
        answer:
          'A vehicle is strongly recommended because accommodation, attractions and services are spread across the district.',
      },
      {
        question: 'What attractions are near Padang Matsirat?',
        answer:
          'The western route provides access towards Langkawi SkyCab, Sky Bridge, Oriental Village and selected rainforest activities.',
      },
      {
        question: 'Who should stay in Padang Matsirat?',
        answer:
          'It suits visitors prioritising airport convenience, short stays or early access to western Langkawi attractions.',
      },
    ],
    sources: [
      {
        label: 'Tourism Malaysia — Langkawi destination',
        url: 'https://www.malaysia.travel/explore/langkawi',
      },
    ],
    factCheckedAt: FACT_CHECKED_AT,
  },
  {
    slug: 'teluk-datai',
    name: 'Teluk Datai',
    label: 'Rainforest bay',
    setting: 'Langkawi’s secluded northwest coast',
    positioning:
      'Choose Teluk Datai for rainforest immersion, low-density resort stays and a quiet bay reached along Langkawi’s winding northwest road.',
    seo: {
      title: 'Teluk Datai Area Guide | Rainforest, Bay & Stays',
      description:
        'Plan Teluk Datai in northwest Langkawi with access guidance, rainforest and bay experiences, resort context and nearby places to explore.',
    },
    coordinates: { lat: 6.4305812, lng: 99.6699089 },
    googleMapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Teluk%20Datai%2C%20Langkawi',
    neighbourhoods: ['Teluk Datai', 'Datai Bay'],
    hero: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/The_Datai_Beach_Langkawi_-_panoramio.jpg',
      alt: 'Rainforest-backed beach and calm bay at Teluk Datai Langkawi',
      caption: 'Teluk Datai brings mature rainforest close to the Andaman Sea.',
      credit: 'alhafis · CC BY 3.0',
      creditUrl:
        'https://commons.wikimedia.org/wiki/File:The_Datai_Beach_Langkawi_-_panoramio.jpg',
    },
    gallery: [
      {
        url: 'https://upload.wikimedia.org/wikipedia/commons/c/c4/Teluk_Datai_-_panoramio.jpg',
        alt: 'Forested coastline and water at Teluk Datai in northwest Langkawi',
        caption: 'The bay’s low-density setting feels far removed from Cenang.',
        credit: 'alhafis · CC BY 3.0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Teluk_Datai_-_panoramio.jpg',
      },
      {
        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/Road_To_Datai_Bay_-_panoramio.jpg/1920px-Road_To_Datai_Bay_-_panoramio.jpg',
        alt: 'Winding rainforest road leading towards Teluk Datai Langkawi',
        caption:
          'The forested approach is part of the area’s sense of arrival.',
        credit: 'alhafis · CC BY 3.0',
        creditUrl:
          'https://commons.wikimedia.org/wiki/File:Road_To_Datai_Bay_-_panoramio.jpg',
      },
    ],
    introduction: [
      'Teluk Datai, also known as Datai Bay, is a secluded northwest area where mature rainforest descends towards a sheltered Andaman Sea coast. Luxury resorts and nature-led experiences define the area more than public commercial streets.',
      'Plan access before travelling. The bay is natural geography, but practical entry to beaches, dining, golf and activities may depend on a confirmed resort stay, reservation or organised experience.',
    ],
    facts: [
      { label: 'Atmosphere', value: 'Private, nature-led and low density' },
      { label: 'Ideal stay', value: 'Rainforest retreat or special occasion' },
      { label: 'Airport', value: 'About 40–50 minutes by winding road' },
      { label: 'Access', value: 'Confirm bookings before travelling' },
    ],
    highlights: [
      {
        title: 'Rainforest immersion',
        description:
          'Mature forest and wildlife shape the atmosphere around stays and organised activities.',
      },
      {
        title: 'Sheltered bay',
        description:
          'The quiet northwest coast contrasts with Langkawi’s busier southern beach districts.',
      },
      {
        title: 'Nature-led stays',
        description:
          'Resort programmes may connect guests with forest walks, coast and conservation interpretation.',
      },
      {
        title: 'Scenic approach',
        description:
          'A winding forest road creates distance and a strong sense of arrival.',
      },
    ],
    bestFor: ['Nature lovers', 'Couples', 'Quiet stays', 'Special occasions'],
    planning: {
      base: 'A destination stay rather than a central touring base; choose it for the setting itself.',
      gettingThere:
        'Arrange a resort transfer, taxi or rental vehicle. The northwest road is winding and journeys should not be rushed.',
      gettingAround:
        'There is no dense walkable town centre. Movement and access depend on the property, reservation or organised experience.',
      bestTime:
        'Morning suits guided nature activities; late afternoon brings softer beach light. Confirm access and weather before the drive.',
    },
    featuredBusinessSlugs: [
      'the-datai-langkawi',
      'langkawi-skycab',
      'berjaya-langkawi-resort',
      'tanjung-rhu-beach',
    ],
    faqs: [
      {
        question: 'Is Teluk Datai the same as Datai Bay?',
        answer:
          'Yes. Teluk Datai is the Malay name for Datai Bay on Langkawi’s northwest coast.',
      },
      {
        question: 'Can the public visit Teluk Datai beach?',
        answer:
          'Do not assume unrestricted drive-up access. Confirm entry through a resort stay, dining or golf reservation, or an organised experience.',
      },
      {
        question: 'How far is Teluk Datai from Langkawi Airport?',
        answer:
          'Allow roughly 40–50 minutes by road, with additional time in rain or when driving the winding northwest section.',
      },
      {
        question: 'Who should stay in Teluk Datai?',
        answer:
          'It best suits nature lovers, couples and travellers who value privacy and a destination resort over walkable nightlife.',
      },
      {
        question: 'What can I combine with Teluk Datai?',
        answer:
          'SkyCab, Sky Bridge and Pantai Kok can fit the wider northwest route, but avoid over-packing because roads are winding and weather can affect plans.',
      },
    ],
    sources: [
      {
        label: 'Tourism Malaysia — Langkawi beaches and cruising',
        url: 'https://ebrochures.malaysia.travel/en/langkawi/8-beaches-cruising/',
      },
    ],
    factCheckedAt: FACT_CHECKED_AT,
  },
]

export const legacyBusinessAreaRedirects: Record<string, string> = {
  'pantai-cenang': 'pantai-cenang',
  'datai-bay': 'teluk-datai',
}

/** Retired business slugs → canonical listing (301 in business route). */
export const legacyBusinessSlugRedirects: Record<string, string> = {
  'langkawi-sky-bridge': 'langkawi-skycab',
  'eagle-square': 'dataran-lang',
}

export const areaBusinessReferences: Record<string, string> = {
  ...legacyBusinessAreaRedirects,
  'pantai-tengah': 'pantai-tengah',
  kuah: 'kuah',
  'padang-matsirat': 'padang-matsirat',
  'teluk-datai': 'teluk-datai',
}

export function getAreaBySlug(slug: string): AreaGuide | undefined {
  return areas.find((area) => area.slug === slug)
}

export function getAreaForReference(reference: string): AreaGuide | undefined {
  const areaSlug = areaBusinessReferences[reference]
  return areaSlug ? getAreaBySlug(areaSlug) : undefined
}
